(() => {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getSlider(component) {
    return component.querySelector('.slideshow.banner[id^="Slider-"]');
  }

  function getThumbButtons(component) {
    return Array.from(component.querySelectorAll('.slider-counter__link--dots[data-slide-index]'));
  }

  function setActiveThumb(component, index, total) {
    const thumbs = getThumbButtons(component);
    thumbs.forEach((b) => b.classList.remove('is-active'));
    const active = thumbs.find((b) => Number(b.dataset.slideIndex) === index);
    if (active) active.classList.add('is-active');

    const currentEl = component.querySelector('.slider-counter--current');
    if (currentEl) currentEl.textContent = String(index + 1);

    const totalEl = component.querySelector('.slider-counter--total');
    if (totalEl) totalEl.textContent = String(total);
  }

  // ===== アニメ =====
  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function stopAnim(slider) {
    if (slider.__animRaf) cancelAnimationFrame(slider.__animRaf);
    slider.__animRaf = null;
    slider.__animRunning = false;

    // アニメ用クラスを確実に外す
    slider.classList.remove('is-peek-animating');
  }

  function animateScrollTo(slider, targetLeft, duration = 560) {
    stopAnim(slider);

    if (prefersReduced) {
      slider.scrollLeft = Math.round(targetLeft);
      return;
    }

    // ★CSS(!important)で scroll-snap を止める
    slider.classList.add('is-peek-animating');

    const startLeft = slider.scrollLeft;
    const diff = targetLeft - startLeft;
    const startTime = performance.now();

    // ほぼ移動が無いなら即終了
    if (Math.abs(diff) < 1) {
      slider.scrollLeft = Math.round(targetLeft);
      slider.classList.remove('is-peek-animating');
      return;
    }

    slider.__animRunning = true;

    const step = (now) => {
      if (!slider.__animRunning) return;

      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = easeInOutCubic(t);

      slider.scrollLeft = Math.round(startLeft + diff * eased);

      if (t < 1) {
        slider.__animRaf = requestAnimationFrame(step);
      } else {
        slider.__animRaf = null;
        slider.__animRunning = false;

        // ★アニメ終了後に snap を戻す
        slider.classList.remove('is-peek-animating');
      }
    };

    slider.__animRaf = requestAnimationFrame(step);
  }

  function centerTo(slider, slideEl, smooth = true) {
    const sliderW = slider.clientWidth;
    const slideW = slideEl.getBoundingClientRect().width;
    const left = slideEl.offsetLeft - (sliderW - slideW) / 2;

    if (!smooth || prefersReduced) {
      // 瞬間移動（補正用）…でも一瞬だけ snap を止める
      slider.classList.add('is-peek-animating');
      slider.scrollLeft = Math.round(left);
      requestAnimationFrame(() => slider.classList.remove('is-peek-animating'));
      return;
    }

    animateScrollTo(slider, left, 560);
  }

  function settleAfterScroll(slider, cb) {
    let t = null;
    const onScroll = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        slider.removeEventListener('scroll', onScroll);
        cb();
      }, 120);
    };
    slider.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function nearestSlide(slider, slides) {
    const center = slider.scrollLeft + slider.clientWidth / 2;
    let best = slides[0];
    let bestDist = Infinity;

    slides.forEach((s) => {
      const c = s.offsetLeft + s.clientWidth / 2;
      const d = Math.abs(c - center);
      if (d < bestDist) {
        bestDist = d;
        best = s;
      }
    });

    return best;
  }

  // Dawnクリックを止めて自前に統一（capture）
  function hijackClicks(component, handlers) {
    const btns = component.querySelectorAll(
      '.slider-button--prev, .slider-button--next, button[name="previous"], button[name="next"]'
    );

    btns.forEach((btn) => {
      btn.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (btn.classList.contains('slider-button--prev') || btn.name === 'previous') handlers.prev();
          if (btn.classList.contains('slider-button--next') || btn.name === 'next') handlers.next();
        },
        true
      );
    });

    getThumbButtons(component).forEach((thumb) => {
      thumb.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          const idx = Number(thumb.dataset.slideIndex);
          if (!Number.isNaN(idx)) handlers.go(idx);
        },
        true
      );
    });
  }

  function setup(component) {
    const slider = getSlider(component);
    if (!slider) return;

    if (slider.__peekLoopInited) return;
    slider.__peekLoopInited = true;

    // 既存クローン掃除
    slider.querySelectorAll('[data-loop-clone="1"]').forEach((n) => n.remove());

    // 本物スライド（mid）
    const realSlides = Array.from(slider.querySelectorAll('.slideshow__slide')).filter(
      (el) => !el.getAttribute('data-loop-set')
    );

    const total = realSlides.length;
    if (total < 2) return;

    realSlides.forEach((s, i) => {
      s.setAttribute('data-loop-real', '1');
      s.setAttribute('data-real-index', String(i));
      s.setAttribute('data-loop-set', 'mid');
    });

    // 前後に複製
    const preClones = realSlides.map((s) => {
      const c = s.cloneNode(true);
      c.setAttribute('data-loop-clone', '1');
      c.setAttribute('data-loop-set', 'pre');
      return c;
    });

    const postClones = realSlides.map((s) => {
      const c = s.cloneNode(true);
      c.setAttribute('data-loop-clone', '1');
      c.setAttribute('data-loop-set', 'post');
      return c;
    });

    preClones.reverse().forEach((c) => slider.insertBefore(c, slider.firstChild));
    postClones.forEach((c) => slider.appendChild(c));

    const allSlides = Array.from(slider.querySelectorAll('.slideshow__slide'));
    const midSlides = allSlides.filter((s) => s.getAttribute('data-loop-set') === 'mid');

    let index = 0;
    let isProgrammatic = false;

    requestAnimationFrame(() => {
      centerTo(slider, midSlides[0], false);
      setActiveThumb(component, index, total);
    });

    function normalizeToMid() {
      const nearest = nearestSlide(slider, allSlides);
      const realIndex = Number(nearest.getAttribute('data-real-index') || '0');
      const set = nearest.getAttribute('data-loop-set');

      index = ((realIndex % total) + total) % total;
      setActiveThumb(component, index, total);

      if (set === 'pre' || set === 'post') {
        const targetMid = midSlides[index];
        centerTo(slider, targetMid, false);
      }
    }

    function goToReal(nextIndex, smooth = true) {
      if (isProgrammatic) return;

      index = ((nextIndex % total) + total) % total;
      setActiveThumb(component, index, total);

      isProgrammatic = true;

      const targetMid = midSlides[index];
      const currentNearest = nearestSlide(slider, allSlides);
      const currentSet = currentNearest.getAttribute('data-loop-set');
      const currentReal = Number(currentNearest.getAttribute('data-real-index') || '0');

      // last -> 0 は post0
      if (currentSet === 'mid' && currentReal === total - 1 && index === 0) {
        const post0 = allSlides.find(
          (s) => s.getAttribute('data-loop-set') === 'post' && Number(s.getAttribute('data-real-index')) === 0
        );
        if (post0) {
          centerTo(slider, post0, smooth);
          settleAfterScroll(slider, () => {
            normalizeToMid();
            isProgrammatic = false;
          });
          return;
        }
      }

      // 0 -> last は preLast
      if (currentSet === 'mid' && currentReal === 0 && index === total - 1) {
        const preLast = allSlides.find(
          (s) => s.getAttribute('data-loop-set') === 'pre' && Number(s.getAttribute('data-real-index')) === total - 1
        );
        if (preLast) {
          centerTo(slider, preLast, smooth);
          settleAfterScroll(slider, () => {
            normalizeToMid();
            isProgrammatic = false;
          });
          return;
        }
      }

      centerTo(slider, targetMid, smooth);
      settleAfterScroll(slider, () => {
        normalizeToMid();
        isProgrammatic = false;
      });
    }

    hijackClicks(component, {
      prev: () => goToReal(index - 1, true),
      next: () => goToReal(index + 1, true),
      go: (i) => goToReal(i, true),
    });

    // ユーザー操作が入ったらアニメ停止
    const cancelOnUser = () => stopAnim(slider);
    slider.addEventListener('wheel', cancelOnUser, { passive: true });
    slider.addEventListener('touchstart', cancelOnUser, { passive: true });
    slider.addEventListener('pointerdown', cancelOnUser, { passive: true });

    // スワイプ後に正規化
    slider.addEventListener(
      'scroll',
      () => {
        if (isProgrammatic) return;
        clearTimeout(slider.__loopTimer);
        slider.__loopTimer = setTimeout(() => normalizeToMid(), 170);
      },
      { passive: true }
    );

    // 自動再生（設定ONの時）
    const shouldAutoplay = String(slider.dataset.autoplay) === 'true';
    const intervalSec = Number(slider.dataset.speed || 5);
    const intervalMs = Math.max(3000, intervalSec * 1000);

    let timer = null;
    function startAutoplay() {
      if (!shouldAutoplay) return;
      if (prefersReduced) return;
      stopAutoplay();
      timer = setInterval(() => {
        if (!isProgrammatic) goToReal(index + 1, true);
      }, intervalMs);
    }
    function stopAutoplay() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    startAutoplay();
    component.addEventListener('mouseenter', stopAutoplay);
    component.addEventListener('mouseleave', startAutoplay);
  }

  function init() {
    document.querySelectorAll('slideshow-component').forEach(setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
